import { useEffect, useState } from "react";
import SkeletonLoader from "shared/loaders/skeleton";
import {
  GetAllSectionsService,
  GetSectionQuestionService,
  DeleteFaqSectionService,
  DeleteFaqQuestionService,
} from "shared/services/faqs";
import CustomAccordion from "shared/components/common/customAccordion";
import styles from "./style.module.scss";
import { Icons } from "assets";
import ConfirmationModal from "shared/modal/confirmation";
import { toastMessage } from "shared/components/common/toast";
import classNames from "classnames";

interface Section {
  id: number;
  title: string;
}

interface Question {
  id: number;
  title: string;
  detail: string;
}

interface FAQsListingProps {
  handleUpdateSection: (sectionId: number) => void;
  handleUpdateQuestion: (questionId: number, question: Question) => void;
}

const FAQsListing = ({
  handleUpdateQuestion,
  handleUpdateSection,
}: FAQsListingProps) => {
  const [openSectionIndex, setOpenSectionIndex] = useState<number | null>(null);
  const [openNestedAccordion, setOpenNestedAccordion] = useState<
    Record<number, number | null>
  >({});
  const [sections, setSections] = useState<Section[]>([]);
  const [questions, setQuestions] = useState<Record<number, Question[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "section" | "question";
    id: number;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleAccordionToggle = (index: number, sectionId: number) => {
    setOpenSectionIndex(openSectionIndex === index ? null : index);
    if (!questions[sectionId]) fetchSectionQuestions(sectionId);
  };

  const handleNestedAccordionToggle = (sectionId: number, questionId: number) =>
    setOpenNestedAccordion((prev) => ({
      ...prev,
      [sectionId]: prev[sectionId] === questionId ? null : questionId,
    }));

  const fetchSections = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllSectionsService(page, 10);
      const fetchedSections = response.data.data || [];
      setSections(fetchedSections);

      if (fetchedSections.length > 0) {
        setOpenSectionIndex(0);
        fetchSectionQuestions(fetchedSections[0].id);
      }

      setError(null);
    } catch (err) {
      setError("Failed to load sections. Please try again later.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionQuestions = async (sectionId: number) => {
    try {
      const response = await GetSectionQuestionService(sectionId);
      setQuestions((prev) => ({
        ...prev,
        [sectionId]: response.data.data || [],
      }));
    } catch (err) {
      console.error(`Error fetching questions for section ${sectionId}:`, err);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      if (deleteTarget.type === "section") {
        await DeleteFaqSectionService(deleteTarget.id);
        setSections((prev) =>
          prev.filter((section) => section.id !== deleteTarget.id)
        );
      } else {
        await DeleteFaqQuestionService(deleteTarget.id);
        setQuestions((prev) => {
          const updatedQuestions = { ...prev };
          Object.keys(updatedQuestions).forEach((key) => {
            // @ts-ignore
            updatedQuestions[key] = updatedQuestions[key].filter(
              // @ts-ignore
              (q) => q.id !== deleteTarget.id
            );
          });
          return updatedQuestions;
        });
      }

      toastMessage(
        "success",
        `${
          deleteTarget.type === "section" ? "Section" : "Question"
        } deleted successfully.`
      );
    } catch (err) {
      toastMessage(
        "error",
        `Failed to delete ${
          deleteTarget.type === "section" ? "section" : "question"
        }. Please try again later.`
      );
    } finally {
      setIsDeleting(false);
      setIsModalOpen(false);
      setDeleteTarget(null);
    }
  };

  useEffect(() => {
    fetchSections();
  }, []);

  return (
    <div className={styles.sectionContainer}>
      {loading ? (
        <SkeletonLoader />
      ) : error ? (
        <div className={styles.errorMessage}>{error}</div>
      ) : (
        sections.map((section, index) => (
          <div key={section.id} className={styles.accordionWrapper}>
            <CustomAccordion
              isOpen={openSectionIndex === index}
              onClick={() => handleAccordionToggle(index, section.id)}
              title={section.title}
              renderRightContent={
                <div className="flex items-center gap-2">
                  <button
                    className={classNames(styles.update_btn, styles.action_btn)}
                    onClick={(e) => {
                      e.stopPropagation();
                      // @ts-ignore
                      handleUpdateSection(section.id, section.title);
                    }}
                  >
                    <Icons.UpdateBlog />
                  </button>
                  <button
                    className={classNames(styles.remove_btn, styles.action_btn)}
                    title="Delete Section"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget({ type: "section", id: section.id });
                      setIsModalOpen(true);
                    }}
                  >
                    <Icons.Cross />
                  </button>
                </div>
              }
            >
              {questions[section.id] ? (
                questions[section.id].map((question) => (
                  <div
                    key={question.id}
                    className={styles.nestedAccordionWrapper}
                  >
                    <CustomAccordion
                      isNested
                      isOpen={openNestedAccordion[section.id] === question.id}
                      onClick={() =>
                        handleNestedAccordionToggle(section.id, question.id)
                      }
                      title={question.title}
                      renderRightContent={
                        <div className="flex items-center gap-1.5">
                          <button
                            className={classNames(
                              styles.update_btn,
                              styles.action_btn
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateQuestion(section.id, question);
                            }}
                          >
                            <Icons.UpdateBlog />
                          </button>
                          <button
                            className={classNames(
                              styles.remove_btn,
                              styles.action_btn
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget({
                                type: "question",
                                id: question.id,
                              });
                              setIsModalOpen(true);
                            }}
                          >
                            <Icons.Cross />
                          </button>
                        </div>
                      }
                    >
                      <div>{question.detail}</div>
                    </CustomAccordion>
                  </div>
                ))
              ) : (
                <SkeletonLoader />
              )}
            </CustomAccordion>
          </div>
        ))
      )}

      <ConfirmationModal
        show={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        handleSubmit={handleDelete}
        loading={isDeleting}
        title="Confirm Deletion"
        subtitle={`Are you sure you want to delete this ${
          deleteTarget?.type === "section" ? "section" : "question"
        }?`}
        actionButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default FAQsListing;
